import type { UseFetchOptions } from '#app'
import type { MaybeRefOrGetter } from 'vue'
import type {
  ApiOptionals,
  ApiResponse,
  ApiResponsePaginated,
  PaginationParams,
  UseApiFetchOptions,
} from '~/types/api.type'
import type { Client } from '~/types/client.type'
import type { MappedOmit } from '~/types/common.type'
import type { NotificationThreshold } from '~/types/notification-threshold.type'
import type { Project, ProjectBillingType } from '~/types/project.type'
import type { User } from '~/types/user.type'
import { defu } from 'defu'
import { objectToSnake } from 'ts-case-convert'

export type ApiProjectGetListParams = {
  isActive?: boolean
  clientId?: number
  searchTerm?: string
  withOwnTimeSpentSec?: boolean
  withTimeSpentSec?: boolean
  withOutstandingInvoiceAmount?: boolean
  additionalProjects?: Project['id'][]
  onlyManaged?: boolean
  withTrashed?: boolean
} & PaginationParams<Project> & ApiProjectGetByIdParams

export type ApiProjectGetListParamsUnPaginated = {
  isActive?: boolean
  clientId?: number
  onlyManaged?: boolean
  noPagination?: true
  additionalProjects?: Project['id'][]
} & ApiProjectGetByIdParams

export interface ApiProjectGetByIdParams {
  withTaskLists?: boolean
  withNotificationThresholds?: boolean
  withClient?: boolean
  withLeads?: boolean
  withScheduledTimeSec?: boolean
}

export type ApiProjectCreateProject = MappedOmit<Project, 'id' | 'createdAt' | 'client' | 'leads' | 'notificationThresholds' | 'ownTimeSpentSec' | 'taskLists' | 'timeSpentSec' | 'billingType' | 'sumPrice' | 'timeLimitSec' | 'isActive' | 'outstandingInvoiceAmount' | 'scheduledTimeSec'> & {
  clientId?: Client['id']
  leads?: User['id'][]
  billingType?: ProjectBillingType
  notificationThresholds?: NotificationThreshold[]
  isActive?: Project['isActive']
  timeLimitSec?: Project['timeLimitSec']
}

export function useApiProjectGetList<T extends ApiProjectGetListParams>(params: MaybeRefOrGetter<T> = {} as T, options: UseApiFetchOptions<ApiResponsePaginated<Project<ApiOptionals<T>>>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponsePaginated<Project<ApiOptionals<T>>>> = { method: 'GET', params }
  return useApiFetch('/projects', defu(notOverridableOptions, options))
}

export function useApiProjectGetListUnpaginated<T extends ApiProjectGetListParamsUnPaginated>(params: MaybeRefOrGetter<T> = {} as T, options: UseApiFetchOptions<ApiResponse<Project<ApiOptionals<T>>[]>> = {}) {
  const paramsWithNoPagination = computed<T>(() => {
    return { ...toValue(params), noPagination: true }
  })
  const notOverridableOptions: UseFetchOptions<ApiResponse<Project<ApiOptionals<T>>[]>> = { method: 'GET', params: paramsWithNoPagination }
  return useApiFetch('/projects', defu(notOverridableOptions, options))
}

export function useApiProjectGetById<T extends ApiProjectGetByIdParams>(id: MaybeRefOrGetter<Project['id']>, params: MaybeRefOrGetter<T> = {} as T, options: UseFetchOptions<ApiResponse<Project<ApiOptionals<T>>>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<Project<ApiOptionals<T>>>> = { method: 'GET', params }
  return useApiFetch(computed(() => `/projects/${toValue(id)}`), defu(notOverridableOptions, options))
}

type ApiProjectResource = Project<'withTaskLists' | 'withClient' | 'withLeads'>
export interface ApiProjectCreateQueryParams {
  createDefaultTaskList?: boolean
}

export function useApiProjectCreate(
  project: MaybeRefOrGetter<ApiProjectCreateProject>,
  queryParams: MaybeRefOrGetter<ApiProjectCreateQueryParams> = {},
  options: UseFetchOptions<ApiResponse<ApiProjectResource>> = {},
) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiProjectResource>> = { method: 'POST', body: project, query: queryParams }
  const overridableOptions: UseFetchOptions<ApiResponse<ApiProjectResource>> = { immediate: false, watch: false }
  return useApiFetch(() => `/projects?${new URLSearchParams(objectToSnake(toValue(queryParams)) as Record<string, string>)}`, defu(notOverridableOptions, options, overridableOptions))
}

export interface ApiProjectImportParams {
  sourceProjectId: Project['id']
  includeTaskLists?: boolean
  includeTasks?: boolean
  includeTimeLimits?: boolean
  includeLeads?: boolean
}

export function useApiProjectImport(
  projectId: MaybeRefOrGetter<Project['id']>,
  params: MaybeRefOrGetter<ApiProjectImportParams>,
  options: UseFetchOptions<ApiResponse<ApiProjectResource>> = {},
) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiProjectResource>> = { method: 'PUT', body: params }
  const overridableOptions: UseFetchOptions<ApiResponse<ApiProjectResource>> = { immediate: false, watch: false }
  return useApiFetch(() => `/projects/${toValue(projectId)}/import`, defu(notOverridableOptions, options, overridableOptions))
}

export function useApiProjectUpdateById(id: MaybeRefOrGetter<Project['id']>, project: MaybeRefOrGetter<Partial<ApiProjectCreateProject>>, options: UseFetchOptions<ApiResponse<ApiProjectResource>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiProjectResource>> = { method: 'PATCH', body: project }
  const overridableOptions: UseFetchOptions<ApiResponse<ApiProjectResource>> = { immediate: false, watch: false }
  return useApiFetch(() => `/projects/${id}`, defu(notOverridableOptions, options, overridableOptions))
}

export function useApiProjectDeleteById(id: MaybeRefOrGetter<Project['id']>, options: UseFetchOptions<never> = {}) {
  const notOverridableOptions: UseFetchOptions<never> = { method: 'DELETE' }
  const overridableOptions: UseFetchOptions<never> = { immediate: false, watch: false }
  return useApiFetch(() => `/projects/${toValue(id)}`, defu(notOverridableOptions, options, overridableOptions))
}
